/**
 * @license
 *
 * Copyright 2011 Google Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */


declare const google: any;

import { google as googleModel, MapLabel as GoogleMapLabel, MapLabelOptions } from '@shared/models/googlemaps.model';

/**
 * @fileoverview Map Label.
 *
 * @author Luke Mahe (lukem@google.com),
 *         Chris Broadfoot (cbro@google.com)
 */

export function getMapLabel(opts: MapLabelOptions): GoogleMapLabel {
  const MapLabel = function (options: MapLabelOptions) {
    /* eslint-disable no-magic-numbers */
    this.set('fontFamily', 'Verdana');
    this.set('fontSize', 12);
    this.set('fontColor', '#000000');
    this.set('strokeWeight', 4);
    this.set('strokeColor', '#ffffff');
    this.set('align', 'center');
    this.set('zIndex', 1000);

    this.setValues(options);
  } as any as new (options: MapLabelOptions) => GoogleMapLabel;

  MapLabel.prototype = new google.maps.OverlayView();
  MapLabel.prototype.changed = function changed(prop: string): void {
    switch (prop) {
      case 'fontFamily':
      case 'fontSize':
      case 'fontColor':
      case 'strokeWeight':
      case 'strokeColor':
      case 'align':
      case 'text':
        return this.drawCanvas();
      case 'maxZoom':
      case 'minZoom':
      case 'position':
        return this.draw();
    }
  };
  MapLabel.prototype.onAdd = function onAdd(): void {
    const canvas = this.canvas = document.createElement('canvas');
    const style = canvas.style;
    style.position = 'absolute';

    const ctx = canvas.getContext('2d');
    ctx.lineJoin = 'round';
    ctx.textBaseline = 'top';

    this.drawCanvas();

    const panes = this.getPanes();
    if (panes) {
      panes.overlayLayer.appendChild(canvas);
    }
  };

  MapLabel.prototype.onRemove = function onRemove(): void {
    const canvas = this.canvas;
    if (canvas && canvas.parentNode) {
      canvas.parentNode.removeChild(canvas);
    }
  };

  MapLabel.prototype.draw = function draw(): void {
    const projection = this.getProjection();

    if (!projection) {
      // The map projection is not ready yet so do nothing
      return;
    }

    if (!this.canvas) {
      // onAdd has not been called yet.
      return;
    }

    const latLng: googleModel.maps.LatLng = this.get('position');
    if (!latLng) {
      return;
    }
    const pos = projection.fromLatLngToDivPixel(latLng);

    const style = this.canvas.style;

    style['top'] = pos.y + 'px';
    style['left'] = pos.x + 'px';

    style['visibility'] = this.getVisible();
  };

  MapLabel.prototype.drawCanvas = function drawCanvas(): void {
    const canvas = this.canvas;
    if (!canvas) {
      return;
    }

    const zIndex = this.get('zIndex');
    const style = canvas.style;
    style.zIndex = zIndex;

    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.strokeStyle = this.get('strokeColor');
    ctx.fillStyle = this.get('fontColor');
    ctx.font = this.get('fontSize') + 'px ' + this.get('fontFamily');

    const strokeWeight = Number(this.get('strokeWeight'));

    const text = this.get('text');
    if (text) {
      if (strokeWeight) {
        ctx.lineWidth = strokeWeight;
        ctx.strokeText(text, strokeWeight, strokeWeight);
      }

      ctx.fillText(text, strokeWeight, strokeWeight);

      const textMeasure = ctx.measureText(text);
      const textWidth = textMeasure.width + strokeWeight;
      style.marginLeft = this.getMarginLeft(textWidth) + 'px';
      // Bring actual text top in line with desired latitude.
      // Cheaper than calculating height of text.
      style.marginTop = '-0.5em';
    }
  };

  MapLabel.prototype.getMarginLeft = function getMarginLeft(textWidth: number): number {
    switch (this.get('align')) {
      case 'left':
        return 0;
      case 'right':
        return -textWidth;
    }
    return textWidth / -2;
  };

  MapLabel.prototype.getVisible = function getVisible(): string {
    const minZoom = this.get('minZoom');
    const maxZoom = this.get('maxZoom');

    if (minZoom === undefined && maxZoom === undefined) {
      return '';
    }

    const map = this.getMap();
    if (!map) {
      return '';
    }

    const mapZoom = map.getZoom();
    if (mapZoom < minZoom || mapZoom > maxZoom) {
      return 'hidden';
    }
    return '';
  };

  return new MapLabel(opts) as GoogleMapLabel;
}
